// DesignPalette.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdViewModule,
  MdLink,
  MdAccountTree,
  MdMergeType,
  MdMenuBook,
  MdClose
} from 'react-icons/md';

const DesignPalette = ({
  activeTab,
  setActiveTab,
  availableEntities,
  erdState,
  setCurrentEntity,
  setCurrentRelationship,
  setCurrentSpecialization,
  setCurrentUnion,
  currentEntity,
  currentRelationship,
  currentSpecialization,
  currentUnion,
  setEditingMode,
  onSaveEvent
}) => {
  const [parsedERDItems, setParsedERDItems] = useState({
    entities: [],
    relationships: [],
    specializations: [],
    unions: []
  });
  const [pendingSaveEvent, setPendingSaveEvent] = useState(null);
  const [deleteError, setDeleteError] = useState(null);   // { title, message } or null
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { item, itemType } or null
  const inputTextRef = useRef(erdState.inputText);

  const tabs = [
    { id: 'entity', title: 'Add Entity', icon: MdViewModule },
    { id: 'relationship', title: 'Add Relationship', icon: MdLink },
    { id: 'specialization', title: 'Add Specialization', icon: MdAccountTree },
    { id: 'union', title: 'Add Union', icon: MdMergeType },
    { id: 'reference', title: 'Reference', icon: MdMenuBook }
  ];

  // Clear form fields and exit editing mode
  const clearFormFields = useCallback((tabId) => {
    // Clear editing mode
    setEditingMode({
      isEditing: false,
      componentType: null,
      originalComponent: null
    });

    // Clear form fields based on tab
    switch (tabId) {
      case 'entity':
        setCurrentEntity({
          name: '',
          isWeak: false,
          attributes: [{ name: '', isKey: false, isPartialKey: false, isMultivalued: false, isDerived: false, subAttributes: '' }]
        });
        break;
      case 'relationship':
        setCurrentRelationship({
          name: '',
          isIdentifying: false,
          participants: [{ entity: '', cardinality: '1', roleName: '', hasTotalParticipation: false }],
          attributes: [{ name: '', subAttributes: '' }]
        });
        break;
      case 'specialization':
        setCurrentSpecialization({
          superEntity: '',
          isDisjoint: true,
          isTotal: false,
          subEntities: ['']
        });
        break;
      case 'union':
        setCurrentUnion({
          subCategoryEntity: '',
          isTotal: false,
          superCategories: ['']
        });
        break;
      default:
        // No action needed for reference tab
        break;
    }
  }, [setEditingMode, setCurrentEntity, setCurrentRelationship, setCurrentSpecialization, setCurrentUnion]);

  // Handle tab click with form clearing
  const handleTabClick = useCallback((tabId) => {
    clearFormFields(tabId);
    setActiveTab(tabId);
  }, [clearFormFields, setActiveTab]);

  // Parse all ERD components from notation
  const parseERDFromNotation = useCallback(() => {
    const notation = erdState.inputText || '';

    // Parse Entities
    const entities = [];
    const entityMatches = notation.match(/\[Entity\]\s*\n([^[]*?)(?=\[|$)/gs);
    if (entityMatches) {
      entityMatches.forEach((match, index) => {
        const lines = match.split('\n').filter(line => line.trim());
        if (lines.length > 1) {
          const headerLine = lines[1].trim();
          const nameMatch = headerLine.match(/^(\w+)(\s+@.*)?$/);
          if (nameMatch) {
            const name = nameMatch[1];
            const types = nameMatch[2] ? nameMatch[2].split('@').filter(t => t.trim()).map(t => t.trim()) : [];

            // Parse attributes
            const separatorIndex = lines.findIndex(line => line.trim() === '--');
            const attributes = [];

            if (separatorIndex > -1 && separatorIndex < lines.length - 1) {
              for (let i = separatorIndex + 1; i < lines.length; i++) {
                const attrLine = lines[i].trim();
                if (attrLine && !attrLine.startsWith('[')) {
                  const attrMatch = attrLine.match(/^([^@(]+)(\([^)]+\))?(\s+@.*)?$/);
                  if (attrMatch) {
                    const attrName = attrMatch[1].trim();
                    const subAttributes = attrMatch[2] ? attrMatch[2].slice(1, -1) : '';
                    const attrTypes = attrMatch[3] ? attrMatch[3].split('@').filter(t => t.trim()).map(t => t.trim()) : [];

                    attributes.push({
                      name: attrName,
                      subAttributes,
                      isKey: attrTypes.includes('key'),
                      isPartialKey: attrTypes.includes('partial_key'),
                      isMultivalued: attrTypes.includes('multivalued'),
                      isDerived: attrTypes.includes('derived')
                    });
                  }
                }
              }
            }

            entities.push({
              id: `entity_${index}`,
              name,
              isWeak: types.includes('weak'),
              attributes: attributes.length > 0 ? attributes : [{ name: '', isKey: false, isPartialKey: false, isMultivalued: false, isDerived: false, subAttributes: '' }],
              originalText: match
            });
          }
        }
      });
    }

    // Parse Relationships
    const relationships = [];
    const relationshipMatches = notation.match(/\[Relationship\]\s*\n([^[]*?)(?=\[|$)/g);
    if (relationshipMatches) {
      relationshipMatches.forEach((match, index) => {
        const lines = match.split('\n').filter(line => line.trim());
        if (lines.length > 1) {
          const headerLine = lines[1].trim();
          const nameMatch = headerLine.match(/^(\w+)(\s+@.*)?$/);
          if (nameMatch) {
            const name = nameMatch[1];
            const types = nameMatch[2] ? nameMatch[2].split('@').filter(t => t.trim()).map(t => t.trim()) : [];

            // Parse participants and attributes
            const separatorIndices = lines.map((line, i) => line.trim() === '--' ? i : -1).filter(i => i !== -1);
            const participants = [];
            const attributes = [];

            if (separatorIndices.length > 0) {
              const participantStart = separatorIndices[0] + 1;
              const participantEnd = separatorIndices.length > 1 ? separatorIndices[1] : lines.length;

              for (let i = participantStart; i < participantEnd; i++) {
                const participantLine = lines[i].trim();
                if (participantLine && !participantLine.startsWith('[')) {
                  // Supports: Entity (N), Entity:role (N), Entity (N) @total_participation
                  const participantMatch = participantLine.match(/^(\w+)(?::(\w+))?\s*\(([^)]+)\)(\s+@.*)?$/);
                  if (participantMatch) {
                    const entity = participantMatch[1];
                    const roleName = participantMatch[2] || '';
                    const cardinality = participantMatch[3];
                    const participantTypes = participantMatch[4] ? participantMatch[4].split('@').filter(t => t.trim()).map(t => t.trim()) : [];

                    participants.push({
                      entity,
                      cardinality,
                      roleName,
                      hasTotalParticipation: participantTypes.includes('total_participation')
                    });
                  }
                }
              }

              // Parse attributes if there's a second separator
              if (separatorIndices.length > 1) {
                const attributeStart = separatorIndices[1] + 1;
                for (let i = attributeStart; i < lines.length; i++) {
                  const attrLine = lines[i].trim();
                  if (attrLine && !attrLine.startsWith('[')) {
                    const attrMatch = attrLine.match(/^([^@(]+)(\([^)]+\))?$/);
                    if (attrMatch) {
                      const attrName = attrMatch[1].trim();
                      const subAttributes = attrMatch[2] ? attrMatch[2].slice(1, -1) : '';
                      attributes.push({
                        name: attrName,
                        subAttributes
                      });
                    }
                  }
                }
              }
            }

            relationships.push({
              id: `relationship_${index}`,
              name,
              isIdentifying: types.includes('identifying'),
              participants: participants.length > 0 ? participants : [{ entity: '', cardinality: '1', roleName: '', hasTotalParticipation: false }],
              attributes: attributes.length > 0 ? attributes : [{ name: '', subAttributes: '' }],
              originalText: match
            });
          }
        }
      });
    }

    // Parse Specializations
    const specializations = [];
    const specializationMatches = notation.match(/\[Specialization\]\s*\n([^[]*?)(?=\[|$)/g);
    if (specializationMatches) {
      specializationMatches.forEach((match, index) => {
        const lines = match.split('\n').filter(line => line.trim());
        if (lines.length > 1) {
          const headerLine = lines[1].trim();
          const nameMatch = headerLine.match(/^(\w+)(\s+@.*)?$/);
          if (nameMatch) {
            const name = nameMatch[1];
            const types = nameMatch[2] ? nameMatch[2].split('@').filter(t => t.trim()).map(t => t.trim()) : [];

            // Parse sub-entities
            const separatorIndex = lines.findIndex(line => line.trim() === '--');
            const subEntities = [];

            if (separatorIndex > -1 && separatorIndex < lines.length - 1) {
              for (let i = separatorIndex + 1; i < lines.length; i++) {
                const subEntityLine = lines[i].trim();
                if (subEntityLine && !subEntityLine.startsWith('[')) {
                  subEntities.push(subEntityLine);
                }
              }
            }

            specializations.push({
              id: `specialization_${index}`,
              name,
              superEntity: name,
              isDisjoint: types.includes('disjoint'),
              isTotal: types.includes('total'),
              subEntities: subEntities.length > 0 ? subEntities : [''],
              originalText: match
            });
          }
        }
      });
    }

    // Parse Unions
    const unions = [];
    const unionMatches = notation.match(/\[Union\]\s*\n([^[]*?)(?=\[|$)/g);
    if (unionMatches) {
      unionMatches.forEach((match, index) => {
        const lines = match.split('\n').filter(line => line.trim());
        if (lines.length > 1) {
          const headerLine = lines[1].trim();
          const nameMatch = headerLine.match(/^(\w+)(\s+@.*)?$/);
          if (nameMatch) {
            const name = nameMatch[1];
            const types = nameMatch[2] ? nameMatch[2].split('@').filter(t => t.trim()).map(t => t.trim()) : [];

            // Parse super-categories
            const separatorIndex = lines.findIndex(line => line.trim() === '--');
            const superCategories = [];

            if (separatorIndex > -1 && separatorIndex < lines.length - 1) {
              for (let i = separatorIndex + 1; i < lines.length; i++) {
                const superCategoryLine = lines[i].trim();
                if (superCategoryLine && !superCategoryLine.startsWith('[')) {
                  superCategories.push(superCategoryLine);
                }
              }
            }

            unions.push({
              id: `union_${index}`,
              name,
              subCategoryEntity: name,
              isTotal: types.includes('total'),
              superCategories: superCategories.length > 0 ? superCategories : [''],
              originalText: match
            });
          }
        }
      });
    }

    return { entities, relationships, specializations, unions };
  }, [erdState.inputText]);

  // Update parsed items when notation changes
  useEffect(() => {
    setParsedERDItems(parseERDFromNotation());
  }, [parseERDFromNotation]);

  // Effect to track inputText changes and trigger pending save events
  useEffect(() => {
    // Only trigger save if inputText actually changed and we have a pending event
    if (erdState.inputText !== inputTextRef.current && pendingSaveEvent && onSaveEvent) {
      inputTextRef.current = erdState.inputText;
      // Use setTimeout to ensure the state has fully propagated
      setTimeout(() => {
        onSaveEvent(pendingSaveEvent);
        setPendingSaveEvent(null);
      }, 0);
    } else {
      inputTextRef.current = erdState.inputText;
    }
  }, [erdState.inputText, pendingSaveEvent, onSaveEvent]);

  // Edit entity - loads data into form and switches to entity tab
  const editEntity = (entity) => {
    setCurrentEntity({
      name: entity.name,
      isWeak: entity.isWeak,
      attributes: entity.attributes
    });

    setEditingMode({
      isEditing: true,
      componentType: 'entity',
      originalComponent: entity
    });
    setActiveTab('entity');
  };

  // Edit relationship - loads data into form and switches to relationship tab
  const editRelationship = (relationship) => {
    setCurrentRelationship({
      name: relationship.name,
      isIdentifying: relationship.isIdentifying,
      participants: relationship.participants,
      attributes: relationship.attributes
    });
    setEditingMode({
      isEditing: true,
      componentType: 'relationship',
      originalComponent: relationship
    });
    setActiveTab('relationship');
  };

  // Edit specialization - loads data into form and switches to specialization tab
  const editSpecialization = (specialization) => {
    setCurrentSpecialization({
      superEntity: specialization.superEntity,
      isDisjoint: specialization.isDisjoint,
      isTotal: specialization.isTotal || false,
      subEntities: specialization.subEntities
    });
    setEditingMode({
      isEditing: true,
      componentType: 'specialization',
      originalComponent: specialization
    });
    setActiveTab('specialization');
  };

  // Edit union - loads data into form and switches to union tab
  const editUnion = (union) => {
    setCurrentUnion({
      subCategoryEntity: union.subCategoryEntity,
      isTotal: union.isTotal || false,
      superCategories: union.superCategories
    });
    setEditingMode({
      isEditing: true,
      componentType: 'union',
      originalComponent: union
    });
    setActiveTab('union');
  };

  // Check whether an entity is referenced by relationships / specializations / unions.
  // Returns an error message string if blocked, or null if safe to delete.
  const getEntityDeleteBlocker = (entityName) => {
    const { relationships, specializations, unions } = parsedERDItems;

    // Check relationships
    for (const rel of relationships) {
      if (rel.participants.some(p => p.entity === entityName)) {
        return `Entity "${entityName}" cannot be deleted because it participates in the "${rel.name}" relationship.`;
      }
    }

    // Check specializations (as superclass or subclass)
    for (const spec of specializations) {
      if (spec.superEntity === entityName) {
        return `Entity "${entityName}" cannot be deleted because it is the superclass in the "${spec.name}" specialization.`;
      }
      if (spec.subEntities.includes(entityName)) {
        return `Entity "${entityName}" cannot be deleted because it is a subclass in the "${spec.name}" specialization.`;
      }
    }

    // Check unions (as subcategory entity or super-category)
    for (const union of unions) {
      if (union.subCategoryEntity === entityName) {
        return `Entity "${entityName}" cannot be deleted because it is the subcategory entity in the "${union.name}" union.`;
      }
      if (union.superCategories.includes(entityName)) {
        return `Entity "${entityName}" cannot be deleted because it is a super-category in the "${union.name}" union.`;
      }
    }

    return null;
  };

  // Delete item from notation
  const deleteItem = (item, itemType) => {
    // For entities, check for dependencies before allowing deletion
    if (itemType === 'entity') {
      const blocker = getEntityDeleteBlocker(item.name);
      if (blocker) {
        setDeleteError({ title: 'Cannot Delete Entity', message: blocker });
        return;
      }
    }
    // Show custom confirmation modal instead of window.confirm
    setDeleteConfirm({ item, itemType });
  };

  // Called when user clicks "Delete" in the confirmation modal
  const confirmDelete = () => {
    if (!deleteConfirm) return;
    const { item, itemType } = deleteConfirm;
    setDeleteConfirm(null);

    const updatedNotation = erdState.inputText.replace(item.originalText, '');
    const cleanedNotation = updatedNotation.replace(/\n\n\n+/g, '\n\n').trim();
    erdState.setInputText(cleanedNotation);
    setPendingSaveEvent(`${itemType}_deleted`);
    clearFormIfMatching(item, itemType);
  };

  // Clear form fields if the deleted item is currently loaded
  const clearFormIfMatching = (deletedItem, itemType) => {
    switch (itemType) {
      case 'entity':
        // Check if the deleted entity matches the currently loaded entity
        if (setCurrentEntity && currentEntity && deletedItem.name === currentEntity.name) {
          setCurrentEntity({
            name: '',
            isWeak: false,
            attributes: [{ name: '', isKey: false, isPartialKey: false, isMultivalued: false, isDerived: false, subAttributes: '' }]
          });
        }
        break;
      case 'relationship':
        // Check if the deleted relationship matches the currently loaded relationship
        if (setCurrentRelationship && currentRelationship && deletedItem.name === currentRelationship.name) {
          setCurrentRelationship({
            name: '',
            isIdentifying: false,
            participants: [{ entity: '', cardinality: '1', roleName: '', hasTotalParticipation: false }],
            attributes: [{ name: '', subAttributes: '' }]
          });
        }
        break;
      case 'specialization':
        // Check if the deleted specialization matches the currently loaded specialization
        if (setCurrentSpecialization && currentSpecialization && deletedItem.name === currentSpecialization.superEntity) {
          setCurrentSpecialization({
            superEntity: '',
            isDisjoint: true,
            isTotal: false,
            subEntities: ['']
          });
        }
        break;
      case 'union':
        // Check if the deleted union matches the currently loaded union
        if (setCurrentUnion && currentUnion && deletedItem.name === currentUnion.subCategoryEntity) {
          setCurrentUnion({
            subCategoryEntity: '',
            isTotal: false,
            superCategories: ['']
          });
        }
        break;
      default:
        // No action needed for unknown types
        break;
    }
  };

  return (
    <>
    <div className="w-48 bg-gray-50 border-r border-gray-200 flex flex-col h-full">
      {/* Palette Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <h3 className="font-semibold text-gray-800 text-sm">Design Palette</h3>
      </div>

      {/* Palette Navigation */}
      <nav className="flex-shrink-0 p-2">
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 mb-1 flex items-center ${activeTab === tab.id
                ? 'bg-blue-100 text-blue-700 font-medium border border-blue-200'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                }`}
            >
              <IconComponent className="w-4 h-4 mr-2" />
              {tab.title}
            </button>
          );
        })}
      </nav>

      {/* Divider */}
      <div className="border-t border-gray-300 flex-shrink-0"></div>

      {/* Scrollable ERD Items Section */}
      <div className="flex-1 overflow-y-auto p-3">
        <h4 className="font-medium text-gray-700 text-xs mb-3">ERD Components</h4>

        {/* Entities Section */}
        {parsedERDItems.entities.length > 0 && (
          <div className="mb-4">
            <h5 className="text-xs font-medium text-gray-600 mb-2">Entities ({parsedERDItems.entities.length})</h5>
            <div className="space-y-1">
              {parsedERDItems.entities.map((entity) => (
                <div key={entity.id} className="group relative">
                  <button
                    onClick={() => editEntity(entity)}
                    className="w-full bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1.5 rounded text-xs flex items-center transition-colors duration-200 pr-8"
                    title={`Edit ${entity.name}`}
                  >
                    <MdViewModule className="w-3 h-3 mr-1 text-blue-600" />
                    <span className="truncate">
                      {entity.name}
                      {entity.isWeak && <span className="text-xs ml-1">(W)</span>}
                    </span>
                  </button>
                  {/* Delete button positioned absolutely to avoid nesting */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteItem(entity, 'entity');
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity duration-200 p-1"
                    title={`Delete ${entity.name}`}
                  >
                    <MdClose className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Relationships Section */}
        {parsedERDItems.relationships.length > 0 && (
          <div className="mb-4">
            <h5 className="text-xs font-medium text-gray-600 mb-2">Relationships ({parsedERDItems.relationships.length})</h5>
            <div className="space-y-1">
              {parsedERDItems.relationships.map((relationship) => (
                <div key={relationship.id} className="group relative">
                  <button
                    onClick={() => editRelationship(relationship)}
                    className="w-full bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1.5 rounded text-xs flex items-center transition-colors duration-200 pr-8"
                    title={`Edit ${relationship.name}`}
                  >
                    <MdLink className="w-3 h-3 mr-1 text-green-600" />
                    <span className="truncate">
                      {relationship.name}
                      {relationship.isIdentifying && <span className="text-xs ml-1">(I)</span>}
                    </span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteItem(relationship, 'relationship');
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity duration-200 p-1"
                    title={`Delete ${relationship.name}`}
                  >
                    <MdClose className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Specializations Section */}
        {parsedERDItems.specializations.length > 0 && (
          <div className="mb-4">
            <h5 className="text-xs font-medium text-gray-600 mb-2">Specializations ({parsedERDItems.specializations.length})</h5>
            <div className="space-y-1">
              {parsedERDItems.specializations.map((specialization) => (
                <div key={specialization.id} className="group relative">
                  <button
                    onClick={() => editSpecialization(specialization)}
                    className="w-full bg-purple-100 hover:bg-purple-200 text-purple-800 px-2 py-1.5 rounded text-xs flex items-center transition-colors duration-200 pr-8"
                    title={`Edit ${specialization.name}`}
                  >
                    <MdAccountTree className="w-3 h-3 mr-1 text-purple-600" />
                    <span className="truncate">
                      {specialization.name}
                      <span className="text-xs ml-1">({specialization.isDisjoint ? 'D' : 'O'})</span>
                    </span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteItem(specialization, 'specialization');
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity duration-200 p-1"
                    title={`Delete ${specialization.name}`}
                  >
                    <MdClose className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Unions Section */}
        {parsedERDItems.unions.length > 0 && (
          <div className="mb-4">
            <h5 className="text-xs font-medium text-gray-600 mb-2">Unions ({parsedERDItems.unions.length})</h5>
            <div className="space-y-1">
              {parsedERDItems.unions.map((union) => (
                <div key={union.id} className="group relative">
                  <button
                    onClick={() => editUnion(union)}
                    className="w-full bg-orange-100 hover:bg-orange-200 text-orange-800 px-2 py-1.5 rounded text-xs flex items-center transition-colors duration-200 pr-8"
                    title={`Edit ${union.name}`}
                  >
                    <MdMergeType className="w-3 h-3 mr-1 text-orange-600" />
                    <span className="truncate">{union.name}</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteItem(union, 'union');
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity duration-200 p-1"
                    title={`Delete ${union.name}`}
                  >
                    <MdClose className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {parsedERDItems.entities.length === 0 &&
          parsedERDItems.relationships.length === 0 &&
          parsedERDItems.specializations.length === 0 &&
          parsedERDItems.unions.length === 0 && (
            <div className="text-center py-4">
              <p className="text-xs text-gray-500">No ERD components yet.</p>
              <p className="text-xs text-gray-400 mt-1">Start by adding entities!</p>
            </div>
          )}
      </div>
    </div>

    {/* Delete confirmation modal */}
    {deleteConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <div className="flex items-start space-x-3 mb-4">
            <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Delete {deleteConfirm.itemType}</h3>
              <p className="text-sm text-gray-600 mt-1">
                Are you sure you want to delete the {deleteConfirm.itemType}{' '}
                <span className="font-medium">"{deleteConfirm.item.name}"</span>? This cannot be undone.
              </p>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Delete-blocked error modal */}
    {deleteError && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <div className="flex items-start space-x-3 mb-4">
            <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{deleteError.title}</h3>
              <p className="text-sm text-gray-600 mt-1">{deleteError.message}</p>
              <p className="text-xs text-gray-500 mt-2">Remove the dependency first, then delete the entity.</p>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setDeleteError(null)}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default DesignPalette;